/**
 * First-version input ceilings.
 *
 * These are the sizes this UI accepts, not a stability guarantee: nothing here claims
 * that a 24 MiB file always succeeds, only that anything above the ceiling is rejected
 * consistently and with the same message every time.
 */

import { AppError } from './errors';

const MiB = 1024 * 1024;

export const LIMITS = {
  maxFileBytes: 25 * MiB,
  maxTotalBytes: 50 * MiB,
  maxTotalPages: 200,
  /** Guard against a folder drop turning into thousands of intake attempts. */
  maxFiles: 20,
} as const;

export const LIMITS_HUMAN = {
  maxFileMiB: LIMITS.maxFileBytes / MiB,
  maxTotalMiB: LIMITS.maxTotalBytes / MiB,
  maxTotalPages: LIMITS.maxTotalPages,
  maxFiles: LIMITS.maxFiles,
} as const;

function toMiB(bytes: number): number {
  return Math.round((bytes / MiB) * 10) / 10;
}

export function checkFileSize(name: string, size: number): void {
  if (size <= 0) {
    throw new AppError('emptyFile', { name });
  }
  if (size > LIMITS.maxFileBytes) {
    throw new AppError('fileTooLarge', {
      name,
      limitMiB: LIMITS_HUMAN.maxFileMiB,
      actualMiB: toMiB(size),
    });
  }
}

export function checkTotalSize(name: string, alreadyLoadedBytes: number, incomingBytes: number): void {
  const total = alreadyLoadedBytes + incomingBytes;
  if (total > LIMITS.maxTotalBytes) {
    throw new AppError('totalTooLarge', {
      name,
      limitMiB: LIMITS_HUMAN.maxTotalMiB,
      actualMiB: toMiB(total),
    });
  }
}

export function checkTotalPages(name: string, alreadyLoadedPages: number, incomingPages: number): void {
  const total = alreadyLoadedPages + incomingPages;
  if (total > LIMITS.maxTotalPages) {
    throw new AppError('tooManyPages', {
      name,
      limitPages: LIMITS.maxTotalPages,
      actualPages: total,
    });
  }
}

export function checkFileCount(alreadyLoadedFiles: number, incomingFiles: number): void {
  if (alreadyLoadedFiles + incomingFiles > LIMITS.maxFiles) {
    throw new AppError('tooManyFiles', { limitFiles: LIMITS.maxFiles });
  }
}
